import type Luminary from '@lumen5/luminary';
import type { Scene, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates/templates-selectors';
import { setCropUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideoOperation } from 'js/src/data/video-data/video-data-operations';
import { getLinkedMediaAssets } from 'js/src/libs/scene-utils';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import {
    getBaseSceneDesignId,
    getPrePackagedMorphPosition,
    hasAppliedMatchCut,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { getEditableMediaAssets } from 'js/src/luminary/media-selectors';
import { setMediaCrop } from 'js/src/luminary/utils';
import {
    getVoicedrivenSceneDuration,
    getVoicedrivenScenesActiveAt,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';

/**
 * How far an animated zoom pushes the media in to start with. The scene it duplicates is otherwise
 * identical, so without this the morph has nothing to animate between.
 */
export const ANIMATED_ZOOM_PUSH_IN_SCALE = 1.25;

/**
 * The lead-in scene is trimmed to this so hovering a tile reaches the zoom almost immediately
 * rather than sitting on a still frame for the source scene's full duration.
 */
const ANIMATED_ZOOM_PREVIEW_LEAD_IN_MS = 600;

/** Far enough past the morph that the resting poster shows the move finished, not mid-blend. */
const POSTER_SETTLE_MS = 700;

export type AnimatedZoomPreviewScenes = {
    /** The pair the tile plays across: the source, then the same thing pushed in. */
    sceneIds: [string, string];
    posterOffsetMs: number;
};

/**
 * Builds the two scenes a tile morphs between, so the preview shows the zoom happening rather than
 * just its end state. The same shape scene-sequence previews use.
 */
export const appendAnimatedZoomPreviewSceneOperation = createLuminaryOperation(
    (
        { luminary },
        leadInSceneId: string,
        populate: (scene: Scene) => void,
    ): AnimatedZoomPreviewScenes | null => {
        const from = luminary.getSceneById(leadInSceneId);
        if (!from) {
            return null;
        }
        // Added straight after the lead-in so the two end up adjacent, which is what lets the morph
        // run between them.
        const { scene: to } = luminary.addSceneGroup({ sceneDesignId: from.sceneDesign });
        populate(to);

        // Luminary skips setting up a morph entirely when the scene carrying it is shorter than the
        // morph, so trimming past that would silently remove the animation the preview exists for.
        from.duration = Math.max(
            Math.min(from.duration, ANIMATED_ZOOM_PREVIEW_LEAD_IN_MS),
            VideoData.DEFAULT_MORPH_DURATION,
        );
        from.morphEnabled = true;

        return {
            sceneIds: [from.id, to.id],
            posterOffsetMs: from.duration + POSTER_SETTLE_MS,
        };
    },
);

/** Creates both halves where the caller has no lead-in scene of its own yet. */
export const buildAnimatedZoomPreviewScenesOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneDesignId: string,
        populate: (scene: Scene) => void,
    ): AnimatedZoomPreviewScenes | null => {
        const { scene: from } = luminary.addSceneGroup({ sceneDesignId });
        populate(from);
        return call(appendAnimatedZoomPreviewSceneOperation, from.id, populate);
    },
);

export function getAnimatedZoomMediaAsset(scene: Scene): TimeAwareMediaAsset | null {
    return getLinkedMediaAssets(scene)[0] ?? getEditableMediaAssets(scene)[0] ?? null;
}

/**
 * Whether a scene can be the one an animated zoom zooms out of. The entry points each build the new scene
 * differently but all arm the same morph, and that morph refuses any gap a scene sequence or an applied match cut
 * already drives — so the offer has to be decided by the same rule, or a tile appears that silently produces a
 * hard cut.
 */
export function canStartAnimatedZoomFrom(
    sourceScene: Scene | null | undefined,
): sourceScene is Scene {
    if (!sourceScene || !getAnimatedZoomMediaAsset(sourceScene)) {
        return false;
    }
    if (getPrePackagedMorphPosition(sourceScene) !== null) {
        return false;
    }
    const precedingScene = sourceScene.prevScene;
    return (
        !hasAppliedMatchCut(sourceScene) && !(precedingScene && hasAppliedMatchCut(precedingScene))
    );
}

export const pushInMediaForAnimatedZoomOperation = createLuminaryOperation(
    async ({ luminary }, sceneId: string): Promise<boolean> => {
        // Luminary re-fits the media to its fill mode when the media finishes loading, which wipes
        // any transform set before then. Wait that out, the same way fitting a screen recording in
        // its scene does, otherwise the media stays at the plain fitted scale.
        await luminary.onCanPlay();

        const scene = luminary.getSceneById(sceneId);
        const asset = scene ? getAnimatedZoomMediaAsset(scene) : null;
        if (!asset) {
            return false;
        }
        setMediaCrop(
            luminary,
            sceneId,
            asset.id,
            asset.xTranslation,
            asset.yTranslation,
            asset.scaleX * ANIMATED_ZOOM_PUSH_IN_SCALE,
            asset.transformation.angle,
            asset.flipHorizontally,
            asset.flipVertically,
        );
        return true;
    },
);

/**
 * The push-in for a real scene rather than a preview tile. Recording it as a user crop stops the
 * recommended scene attributes resetting it back to a plain fit.
 */
export const applyAnimatedZoomPushInOperation = createLuminaryOperation(
    async (
        { call, dispatch }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        parentActionGroup?: string,
    ) => {
        if (!(await call(pushInMediaForAnimatedZoomOperation, sceneId))) {
            return;
        }
        dispatch(
            setCropUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered: true,
                parentActionGroup,
            }),
        );
        // The scene was checkpointed before the media finished loading, so the zoom needs its own.
        // Sharing the action group keeps it part of the same undo step as creating the scene.
        call(checkpointVideoOperation, { systemTriggered: true, parentActionGroup });
    },
);

/** Voicedriven scenes own their duration through the transcript, so `load` must not take it over. */
export const copySceneContentForAnimatedZoomOperation = createLuminaryOperation(
    (
        { luminary },
        savedSourceScene: ReturnType<Scene['save']>,
        targetSceneId: string,
        isVoicedriven: boolean,
    ) => {
        const target = luminary.getSceneById(targetSceneId);
        if (!target) {
            return;
        }
        const duration = isVoicedriven ? getVoicedrivenSceneDuration(target) : null;
        target.load(savedSourceScene);
        target.generateNewAssetIds();
        if (duration !== null) {
            setVoicedrivenSceneDuration(target, duration);
        }
    },
);

type AnimatedZoomOverlaySource = {
    scene: Scene | null;
    sceneDesignId: string | null;
};

/**
 * What an animated-zoom overlay starting at `videoTime` should duplicate. An overlay already
 * covering that point is the thing the zoom continues from; otherwise there is nothing to repeat
 * but the base media itself.
 */
export const getAnimatedZoomOverlaySourceOperation = createLuminaryOperation(
    (
        { luminary, getState },
        sceneGroupId: string,
        videoTime: number | null,
    ): AnimatedZoomOverlaySource => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup) || videoTime === null) {
            return { scene: null, sceneDesignId: null };
        }

        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        const activeScene = getVoicedrivenScenesActiveAt(sceneGroup, videoTime).at(-1) ?? null;
        if (activeScene && activeScene.sceneDesign !== baseSceneDesignId) {
            return { scene: activeScene, sceneDesignId: activeScene.sceneDesign };
        }

        return {
            scene: null,
            sceneDesignId: getTemplateDefaultCutawaySceneDesign(getState(), luminary.template.id),
        };
    },
);
